// components/UpdateBanner.js
// Update announcement for the installed tablet app. The web app updates itself
// via the service worker, but the native shell (APK) can only be updated by
// installing a new build from /download — this banner is how the tablet finds
// out one exists. Shows only in tablet mode, compares the installed versionCode
// (see tabletMode.getInstalledApkVersion) against /api/app-version, and can be
// snoozed for an hour per version. The snooze is short and Later sits far from
// Update because a mis-tap on the floor hid the update for a full day (08-10-2026).
// Hidden on MDM-managed tablets (shell 2.2+): the MDM delivers the update instead.
import React, { useEffect, useState } from 'react';
import { isTabletMode, getInstalledApkVersion } from '../utils/tabletMode';
import { hasNativeUploader, nativeOpenDownloadPage, nativeGetAppInfo } from '../utils/nativeBridge';

const SNOOZE_MS = 60 * 60 * 1000;

export default function UpdateBanner() {
  const [latest, setLatest] = useState(null);
  const [installed, setInstalled] = useState(null);

  useEffect(() => {
    if (!isTabletMode()) return;
    let alive = true;
    (async () => {
      // The Capacitor shell knows its exact installed version; the old TWA
      // falls back to the apkv URL param (absent = the original build, v1).
      const info = await nativeGetAppInfo();
      // MDM-managed scanners (Knox, later Esper) get updates silently from the MDM, and the
      // locked-down scanner blocks the browser this banner would open.
      if (info?.managed) return;
      const installedCode = info?.versionCode || getInstalledApkVersion();
      const res = await fetch('/api/app-version').catch(() => null);
      const data = res?.ok ? await res.json().catch(() => null) : null;
      if (alive && data?.versionCode) {
        setInstalled(installedCode);
        setLatest(data);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!latest || latest.versionCode <= installed) return null;

  const snoozeKey = `apk_update_snoozed_${latest.versionCode}`;
  const snoozedAt = parseInt(localStorage.getItem(snoozeKey), 10);
  if (snoozedAt && Date.now() - snoozedAt < SNOOZE_MS) return null;

  const openDownload = async () => {
    // The Capacitor WebView can't handle APK downloads itself; hand off to the
    // system browser. The old TWA is Chrome, so in-app navigation works there.
    const handled = hasNativeUploader() && (await nativeOpenDownloadPage());
    if (!handled) window.location.href = '/download';
  };

  const snooze = () => {
    localStorage.setItem(snoozeKey, String(Date.now()));
    setLatest(null);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', flexWrap: 'wrap',
      background: 'var(--accent-primary, #3b82f6)', color: '#fff',
      fontSize: '0.8125rem', fontWeight: 500,
    }}>
      <span style={{ flex: 1 }}>
        ⬆ Tablet app update available (v{latest.versionName || latest.versionCode})
        {latest.notes ? ` — ${latest.notes}` : ''}
      </span>
      <button onClick={openDownload} style={{
        background: '#fff', color: '#1a1f2e', border: 'none', borderRadius: 6,
        padding: '10px 22px', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer',
      }}>
        Update
      </button>
      <button onClick={snooze} style={{
        background: 'transparent', color: 'rgba(255,255,255,0.8)', border: 'none',
        fontSize: '0.8125rem', cursor: 'pointer', padding: '10px 8px', marginLeft: 48,
      }}>
        Later
      </button>
    </div>
  );
}
